import { useMemo } from 'react';
import type { DCSContext, SlotPlacementId, Step } from '@de/types/types';
import { generateCorrelationIds } from './correlationIds';
import {
  buildEventContext,
  type DynamicExperienceEventContext,
} from './buildEventContext';
import type { CustomDimension } from './customDimensions';
import type { MonarchIdentity } from './monarchIdentity';

export interface UseDynamicExperienceEventingParams {
  steps: Step[];
  currentStep?: Step;
  currentStepType?: string;
  currentStepIndex?: number;
  dcs?: DCSContext;
  placementId?: SlotPlacementId;
  /**
   * Caller-provided seed for the app-level `correlationId`.
   *
   * **Read once on mount.** Subsequent changes to this prop are ignored until
   * the host component remounts — the entry-point event chain is anchored to
   * a stable per-mount identity (cf. `viewCorrelationId`, generated only on
   * mount). If you need a different correlation ID, remount the consumer.
   */
  correlationId?: string;
  /**
   * Caller-provided seed for `elementLoadedCorrelationId`. **Read once on
   * mount** — see `correlationId` for the rationale.
   */
  elementLoadedCorrelationId?: string;
  /**
   * Caller-provided seed for `formViewedCorrelationId`. **Read once on
   * mount.** When omitted, the value is taken from a `formViewedCorrelationId`
   * URL query param (if present) so cross-page navigations stay correlated;
   * otherwise a fresh UUID is generated.
   */
  formViewedCorrelationId?: string;
  /** Caller-provided dimensions merged into the entry-point `customDimensions` array. */
  providedCustomDimensions?: CustomDimension[];
  // Raw Monarch identity; `slotId`/`experienceVariant` are derived in-package.
  monarch?: MonarchIdentity;
}

export interface UseDynamicExperienceEventingResult {
  correlationId: string;
  elementLoadedCorrelationId: string;
  viewCorrelationId: string;
  formViewedCorrelationId: string;
  eventContext: DynamicExperienceEventContext;
}

/**
 * Owns the entry-point-event lifecycle bits that previously lived inline in
 * `DynamicExperienceProvider`:
 *
 * 1. Generates / accepts-as-seed the four correlation IDs.
 * 2. Builds the `eventContext` consumed by every dispatcher hook
 *    (`useFlowTrackingEvents`, `useViewEvents`, `useHandleStepForward`,
 *     `useHandleSubmit`, etc.).
 *
 * The hook is independently testable — pass a `steps` fixture and props, then
 * assert on the returned `eventContext`. No provider required.
 */
export const useDynamicExperienceEventing = ({
  steps,
  currentStep,
  currentStepType,
  currentStepIndex,
  dcs,
  placementId,
  correlationId: providedCorrelationId,
  elementLoadedCorrelationId: providedElementLoadedCorrelationId,
  formViewedCorrelationId: providedFormViewedCorrelationId,
  providedCustomDimensions,
  monarch,
}: UseDynamicExperienceEventingParams): UseDynamicExperienceEventingResult => {
  const ids = useMemo(
    () =>
      generateCorrelationIds({
        correlationId: providedCorrelationId,
        elementLoadedCorrelationId: providedElementLoadedCorrelationId,
        formViewedCorrelationId: providedFormViewedCorrelationId,
      }),
    // Intentional empty deps: correlation IDs are anchored to the host
    // component's mount lifetime. Re-generating them on prop change would
    // break the correlation chain (e.g. `viewCorrelationId` is shared across the
    // ProductLoaded → ProductViewed → ProductClicked sequence). Callers that
    // need a different ID should remount the consumer. See JSDoc on
    // `UseDynamicExperienceEventingParams.correlationId` for the contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const eventContext = useMemo(
    () =>
      buildEventContext({
        steps,
        currentStep,
        currentStepType,
        currentStepIndex,
        dcs,
        placementId,
        elementLoadedCorrelationId: ids.elementLoadedCorrelationId,
        viewCorrelationId: ids.viewCorrelationId,
        formViewedCorrelationId: ids.formViewedCorrelationId,
        providedCustomDimensions,
        monarch,
      }),
    // Depend on `monarch`'s primitives, not object identity (rebuilt each render).
    [
      steps,
      currentStep,
      currentStepType,
      currentStepIndex,
      dcs,
      placementId,
      ids.elementLoadedCorrelationId,
      ids.viewCorrelationId,
      ids.formViewedCorrelationId,
      providedCustomDimensions,
      monarch?.slotName,
      monarch?.slotVariant,
      monarch?.experienceName,
    ],
  );

  return {
    correlationId: ids.correlationId,
    elementLoadedCorrelationId: ids.elementLoadedCorrelationId,
    viewCorrelationId: ids.viewCorrelationId,
    formViewedCorrelationId: ids.formViewedCorrelationId,
    eventContext,
  };
};
